import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRICING } from "@/components/landing/content";

export default function Pricing() {
  const scrollToContact = (pkgName) => {
    // store package in sessionStorage for the form to read
    try {
      sessionStorage.setItem("jt_selected_package", pkgName);
    } catch {
      /* ignore storage errors */
    }
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 bg-slate-50 border-y border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="max-w-3xl">
          <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-orange-600">
            Package deals
          </span>
          <h2
            data-testid="pricing-heading"
            className="font-display mt-3 text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]"
          >
            Simple monthly pricing.
            <br className="hidden md:block" />
            <span className="brand-text-gradient">No surprises.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl font-body">
            Start small, scale when you’re ready. Every plan is month-to-month
            and tailored to your business goals.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {PRICING.map((p) => (
            <div
              key={p.name}
              data-testid={p.testid}
              className={`relative rounded-3xl p-7 md:p-9 lift border ${
                p.highlight
                  ? "bg-white border-orange-500 shadow-[0_24px_60px_-24px_rgba(234,88,12,0.45)]"
                  : "bg-white border-slate-200"
              }`}
            >
              {p.highlight && (
                <span
                  data-testid="pricing-popular-badge"
                  className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-orange-600 px-3 py-1 text-xs font-bold tracking-wide text-white"
                >
                  <Sparkles size={12} />
                  Most popular
                </span>
              )}

              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-2xl font-extrabold text-slate-900">
                  {p.name}
                </h3>
                <div className="text-right">
                  <span className="text-sm font-semibold text-slate-500">
                    starts at
                  </span>
                </div>
              </div>

              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-5xl md:text-6xl font-extrabold text-slate-900">
                  ${p.price}
                </span>
                <span className="pb-2 text-slate-500 font-medium">
                  {p.cadence}
                </span>
              </div>
              <p className="mt-2 text-slate-600 font-body">{p.tagline}</p>

              <div className="my-6 h-px bg-slate-200" />

              <ul className="space-y-3">
                {p.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5 text-slate-700 font-body"
                  >
                    <span
                      className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full ${
                        p.highlight
                          ? "bg-orange-600 text-white"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => scrollToContact(p.name)}
                data-testid={`${p.testid}-cta`}
                className={`mt-8 w-full rounded-full font-semibold h-11 btn-shine ${
                  p.highlight
                    ? "bg-orange-600 hover:bg-orange-700 text-white"
                    : "bg-slate-900 hover:bg-slate-800 text-white"
                }`}
              >
                {p.cta}
              </Button>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">
          Not sure which plan fits? <a href="#contact" data-testid="pricing-help-link" className="font-semibold text-slate-900 underline decoration-orange-400 underline-offset-4">Tell us about your business →</a>
        </p>
      </div>
    </section>
  );
}
